import { ParsedUrlQuery } from 'querystring';

import React, { useEffect, useMemo, useState } from 'react';

import Image from 'next/image';
import moment from 'moment';
import { useSearchParams } from 'next/navigation';

import Tooltip from '@components/ui/Tooltip/next';
import Typography from '@components/Typography';
import CustomIcon from '@components/utils/CustomIcon';
import { ProfileReservation, ChevronRight } from '@assets/icons';
import { URL_DATE_FORMAT } from '@utils/dateUtil';
import {
  findClosestDate,
  groupDatesByMonth,
} from '@components/views/hotel/listing/components/Events/helpers';
import Markdown from '@components/utils/Markdown';
import { HotelEventsViewModel } from '@api/v3';
import { paramsToObject } from '@components/views/utils';

import {
  Container,
  ContentWrapper,
  DescriptionWrapper,
  DetailsInfo,
  EventsDatesWrapper,
  FlexDatesWrapper,
  ImageWrapper,
  TitleContent,
} from './styled';

type EventItemProps = Partial<HotelEventsViewModel> & {
  isSingleEvent?: boolean;
  toggleDetailModal: () => void;
};

const EventItem = ({
  image,
  title,
  description,
  dates = [],
  isSingleEvent = false,
  toggleDetailModal,
}: EventItemProps) => {
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);
  const { in: startDate } = query;
  const formattedUnDates = useMemo(() => {
    const originalDate = moment(startDate ?? '', URL_DATE_FORMAT);
    const formattedDate = originalDate?.format('YYYY-MM-DDTHH:mm:ss[Z]');
    const closetsDate = findClosestDate(dates, formattedDate);

    const groupByMonthDates = groupDatesByMonth(dates);

    if (!closetsDate) return groupByMonthDates?.split(', ');

    const filteredDates = dates.filter(
      (date) =>
        moment(date).format(URL_DATE_FORMAT) !==
        closetsDate.format(URL_DATE_FORMAT)
    );

    return groupDatesByMonth([
      closetsDate.utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
      ...filteredDates,
    ])?.split(', ');
  }, [dates, startDate]);

  const firstDate = formattedUnDates?.[0];
  const formattedDates = useMemo(() => {
    const originalDate = moment(startDate ?? '', URL_DATE_FORMAT);
    const formattedDate = originalDate?.format('YYYY-MM-DDTHH:mm:ss[Z]');
    const closetsDate = findClosestDate(dates, formattedDate);

    const groupByMonthDates = groupDatesByMonth(dates);

    if (!closetsDate) return groupByMonthDates?.split(', ');

    const filteredDates = dates.filter(
      (date) =>
        moment(date).format(URL_DATE_FORMAT) !==
        closetsDate.format(URL_DATE_FORMAT)
    );

    return groupDatesByMonth([
      closetsDate.utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
      ...filteredDates,
    ])?.split(', ');
  }, [dates, startDate]);

  const otherDates = formattedDates?.slice(1);
  const [truncatedText, setTruncatedText] = useState('');
  const [closestDate, setClosestDate] = useState('');
  const [sameMonthDates, setSameMonthDates] = useState<string[]>([]);

  useEffect(() => {
    const findClosestFutureDateAndSameMonth = () => {
      const now = moment(query.in ?? '', 'YYYY-MM-DD').startOf('day'); // Query tarihini gün başlangıcına çek
      if (!now.isValid()) return { closest: '', sameMonthDates: [] }; // Geçersizse boş döndür

      // Tüm tarihleri Moment objesine çevir ve gün başlangıcına sıfırla
      const futureDates = (dates ?? [])
        ?.map((date) => moment.utc(date).startOf('day')) // Tarihleri UTC’ye çevirip gün başlangıcına sıfırla
        .filter((date) => date.isAfter(now)); // Sadece bugünden SONRAKİ günleri al

      if (futureDates.length === 0) return { closest: '', sameMonthDates: [] }; // Eğer gelecek tarih yoksa boş döndür

      // En yakın gelecekteki tarihi bul
      const closest = futureDates.reduce((prev, curr) =>
        curr.isBefore(prev) ? curr : prev
      );

      // En yakın tarihin yıl ve ay bilgilerini al
      const closestMonth = closest.month();
      const closestYear = closest.year();

      // Aynı yıl ve ay içindeki tüm gelecekteki tarihleri filtrele
      const sameMonthDates = futureDates.filter(
        (date) => date.year() === closestYear && date.month() === closestMonth
      );

      return { closest: closest.toISOString(), sameMonthDates };
    };

    const { closest, sameMonthDates } = findClosestFutureDateAndSameMonth();
    setClosestDate(closest); // En yakın gelecekteki tarihi ayarla
    setSameMonthDates(sameMonthDates.map((date) => date.toISOString())); // Aynı ay içindeki tüm gelecekteki tarihleri ayarla
  }, [query.in, dates]); // `dates` değiştiğinde de çalışması için bağımlılıklara ekledik

  useEffect(() => {
    const truncateText = (text, maxLines = 2) => {
      const lines = text?.split('\n');
      return lines?.slice(0, maxLines).join('\n');
    };

    setTruncatedText(truncateText(description));
  }, [description]);
  return (
    <Container>
      {image ? (
        <ImageWrapper>
          <Image src={image ?? ''} alt={title ?? ''} width={200} height={200} />
        </ImageWrapper>
      ) : null}

      <ContentWrapper>
        <Tooltip
          arrowColor='white'
          overlayInnerStyle={{
            borderRadius: '8px',
            width: '250px',
            lineHeight: 1.43,
            textAlign: 'center',
            backgroundColor: 'white',
          }}
          placement='topLeft'
          title={
            <Typography variant='caption2' weight='Regular' color='D400'>
              <Markdown text={title} />
            </Typography>
          }
        >
          <Typography variant='caption' weight='SemiBold' color='D400'>
            <TitleContent>
              <Markdown text={title} />
            </TitleContent>
          </Typography>
        </Tooltip>
        <DescriptionWrapper>
          <Typography
            variant='caption2'
            weight='Regular'
            color='D100'
            style={{ whiteSpace: 'pre-wrap' }}
          >
            <Markdown text={truncatedText} />
          </Typography>
        </DescriptionWrapper>

        <EventsDatesWrapper
          style={{
            maxWidth: isSingleEvent ? '240px' : '100%',
          }}
        >
          <FlexDatesWrapper>
            <CustomIcon
              icon={ProfileReservation}
              fontSize={24}
              paletteColor='D400'
            />

            <Typography variant='caption2' weight='Regular' color='D400'>
              {closestDate ? groupDatesByMonth(sameMonthDates) : firstDate}
            </Typography>
          </FlexDatesWrapper>

          <FlexDatesWrapper onClick={toggleDetailModal} hasHover>
            {otherDates?.length ? (
              <Typography variant='caption3' weight='Regular' color='B300'>
                Diğer Tarihler
              </Typography>
            ) : null}

            <CustomIcon icon={ChevronRight} fontSize={16} paletteColor='B300' />
          </FlexDatesWrapper>
        </EventsDatesWrapper>

        <DetailsInfo type='button' onClick={toggleDetailModal}>
          <Typography variant='caption2' weight='Bold' color='D400'>
            Detaylı Bilgi
          </Typography>
        </DetailsInfo>
      </ContentWrapper>
    </Container>
  );
};

export default EventItem;
